// src/hooks/use-pwa-install.ts
import { useEffect, useState, useCallback } from 'react';
import { ENVIRONMENT } from '../env';
import {
  checkStandalone,
  manualInstallPlatform,
  detectPlatform,
  ManualPlatform,
} from '../helpers/pwa';
import { isNative } from '../platform';

type DeferredInstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

export function usePwaInstall() {
  // On native Capacitor, the app is always "installed"
  if (isNative()) {
    return {
      isInstalled: true, isStandalone: true, isPwa: true, isBrowser: false,
      canPromptProgrammatically: false, promptInstall: async () => false,
      isIos: false, isAndroid: false, isFirefox: false, isDesktop: false,
      isIosStandalone: false, isIosBrowser: false,
      isAndroidStandalone: false, isDesktopStandalone: false,
      manualPlatform: null as ManualPlatform,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    };
  }

  const [deferredPrompt, setDeferredPrompt] = useState<DeferredInstallPrompt | null>(null);

  // IMPORTANT: keep this meaning = "am I running in standalone right now?"
  const [isInstalled, setIsInstalled] = useState<boolean>(() => {
    const standalone = checkStandalone();
    return standalone;
  });

  const [platform] = useState(() => detectPlatform());

  const [manualPlatformValue] = useState<ManualPlatform | null>(() => manualInstallPlatform());

  // capture beforeinstallprompt once
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onBIP = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as DeferredInstallPrompt);
    };

    const onInstalled = () => {
      const standalone = checkStandalone();
      setIsInstalled(standalone);
    };

    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // also re-check standalone when tab becomes active (covers iOS open from home screen)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const standalone = checkStandalone();
        setIsInstalled(standalone);
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // SANITY: re-check after initial mount too (helps in some iOS/Android edge cases)
  useEffect(() => {
    const standalone = checkStandalone();
    setIsInstalled(standalone);

    // iOS-specific: Sometimes standalone detection needs a moment to settle
    if (platform.isIos && !standalone) {
      const timer = setTimeout(() => {
        const delayed = checkStandalone();
        if (delayed !== standalone) {
          setIsInstalled(delayed);
        }
      }, 200);
      
      return () => clearTimeout(timer);
    }

    return;
  }, [platform.isIos]);

  // reusable install action
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        const standalone = checkStandalone();
        setIsInstalled(standalone);
        return true;
      }

      return false;
    } catch (err) {
      console.error('[PWA] Error during install prompt:', err);
      return false;
    }
  }, [deferredPrompt]);

  // -------- extra flags you can pass down / use in components --------
  const isStandalone = isInstalled; // alias for clarity
  const isPwa = isInstalled;
  const isBrowser = !isInstalled;

  const isIos = platform.isIos;
  const isAndroid = platform.isAndroid;
  const isFirefox = platform.isFirefox;
  const isDesktop = platform.isDesktop;

  const isIosStandalone = isIos && isStandalone;
  const isIosBrowser = isIos && !isStandalone;

  const isAndroidStandalone = isAndroid && isStandalone;
  const isDesktopStandalone = isDesktop && isStandalone;

  const canPromptProgrammatically = !!deferredPrompt;

  if (ENVIRONMENT === 'DEV') console.log('[PWA][usePwaInstall] hook state snapshot:', {
    isInstalled,
    isStandalone,
    isPwa,
    isBrowser,
    canPromptProgrammatically,
    hasDeferredPrompt: !!deferredPrompt,
    manualPlatform: manualPlatformValue,
    isIos,
    isAndroid,
    isFirefox,
    isDesktop,
    userAgent: platform.userAgent,
    isIosStandalone,
    isIosBrowser,
    isAndroidStandalone,
    isDesktopStandalone,
  });

  return {
    // install state
    isInstalled, // true if in standalone / home-screen mode
    isStandalone, // alias
    isPwa, // same as isInstalled / isStandalone
    isBrowser, // true if NOT in standalone

    // browser prompt capability (Android / desktop Chromium)
    canPromptProgrammatically,
    promptInstall,

    // platform hints
    manualPlatform: manualPlatformValue, // 'ios' | 'firefox' | 'android' | 'desktop' | 'other' | null
    isIos,
    isAndroid,
    isFirefox,
    isDesktop,
    userAgent: platform.userAgent,

    // more granular flags you can pass down / use:
    isIosStandalone, // iOS, opened from icon
    isIosBrowser, // iOS, running in Safari tab
    isAndroidStandalone, // Android, installed PWA
    isDesktopStandalone, // Desktop, installed PWA
  };
}
